/**
 * pages.js — plain-data registry of the SEO/landing marketing pages.
 *
 * This file is intentionally dependency-free (no React, no JSX imports) so it can
 * be imported from BOTH the browser bundle (via routes.js, which attaches the
 * lazy Component) AND the Node build-time prerender/sitemap script
 * (scripts/prerender.mjs). It is the single source of truth for each page's path,
 * <head> copy, and sitemap priority — add a page here (+ a Component in routes.js).
 *
 * changefreq/priority feed public/sitemap.xml, which is generated from this data.
 */
export const MARKETING_PAGES = [
  // ── Money pages ──
  { path: '/ai-email-writer',
    title: 'AI Email Writer for Gmail — Tone, Not Just Grammar | PersonaDraft',
    description: 'Persona is the AI email writer that nails tone, not grammar. Describe your intent, pick a persona, and get a perfectly toned email in one click inside Gmail. Free to start.',
    changefreq: 'weekly', priority: 0.9 },
  { path: '/gmail-ai',
    title: 'Gmail AI Extension — One-Click, Context-Aware Replies | PersonaDraft',
    description: 'Add AI to Gmail with Persona. It reads your thread and drafts a perfectly toned reply in one click — no copy-paste. Privacy-first and free to start.',
    changefreq: 'weekly', priority: 0.9 },
  { path: '/email-tone',
    title: "Email Tone Generator — Change Your Email's Tone | PersonaDraft",
    description: "Adjust your email's tone in seconds. Persona dials warmth, assertiveness, and formality until your message lands right. More than a tone checker — it drafts the email.",
    changefreq: 'weekly', priority: 0.9 },
  { path: '/write-professional-emails',
    title: 'Professional Email Writer — Sound Polished, Not Stiff | PersonaDraft',
    description: 'Write professional emails that sound polished without sounding cold. Persona tunes register and tone for any work situation, right inside Gmail. Free to start.',
    changefreq: 'weekly', priority: 0.9 },

  // ── Use-case pages ──
  { path: '/for/non-native-english',
    title: 'AI Email Help for Non-Native English Speakers | PersonaDraft',
    description: 'Sound fluent and confident in every email. Persona turns your intent into natural, perfectly toned English — without second-guessing every word.',
    changefreq: 'monthly', priority: 0.8 },
  { path: '/for/sales',
    title: 'AI Email Writer for Sales & Outreach | PersonaDraft',
    description: 'Outreach that reads right gets answered. Persona tunes the tone of cold emails and replies so they land — built for SDRs, founders, and sales teams.',
    changefreq: 'monthly', priority: 0.8 },
  { path: '/for/executives',
    title: 'AI Email Writer for Executives & Managers | PersonaDraft',
    description: 'High-stakes emails, perfectly toned. Persona helps executives sound decisive without being cold — board, skip-level, and hard-feedback messages.',
    changefreq: 'monthly', priority: 0.8 },
  { path: '/for/recruiters',
    title: 'AI Email Writer for Recruiters | PersonaDraft',
    description: 'Warm, efficient candidate and client emails at volume. Persona tunes tone for outreach, updates, and rejections that keep the relationship.',
    changefreq: 'monthly', priority: 0.8 },
  { path: '/for/consultants',
    title: 'AI Email Writer for Consultants & Agencies | PersonaDraft',
    description: 'Client-facing tone is your product. Persona helps consultants manage expectations and deliver hard news diplomatically — perfectly toned, every time.',
    changefreq: 'monthly', priority: 0.8 },
  { path: '/cultural-intelligence',
    title: 'Cross-Cultural Email — Cultural Intelligence | PersonaDraft',
    description: 'Email norms differ across cultures. Persona adapts tone and register to your recipient — from US directness to Japanese and German business etiquette.',
    changefreq: 'monthly', priority: 0.8 },

  // ── Comparison pages ──
  { path: '/vs/grammarly',
    title: 'PersonaDraft vs Grammarly — Tone, Not Just Grammar',
    description: 'Grammarly fixes words; Persona engineers how your email lands. See how the behavioral AI email writer compares to Grammarly, right inside Gmail.',
    changefreq: 'monthly', priority: 0.7 },
  { path: '/vs/gemini',
    title: 'PersonaDraft vs Gemini Help Me Write — Email AI Compared',
    description: 'Gemini formalizes and shortens; Persona understands the relationship and thread context. Compare the two AI email writers for Gmail.',
    changefreq: 'monthly', priority: 0.7 },
  { path: '/vs/chatgpt-for-email',
    title: 'PersonaDraft vs ChatGPT for Email — Compared',
    description: 'ChatGPT drafts text; Persona lives in Gmail with personas, behavioral dials, thread context, and PII privacy. See why purpose-built email AI wins.',
    changefreq: 'monthly', priority: 0.7 },
  { path: '/vs/lavender',
    title: 'PersonaDraft vs Lavender — AI Email Compared',
    description: 'Lavender scores sales emails; Persona drafts perfectly toned emails for every professional dynamic, right inside Gmail. Compare the two.',
    changefreq: 'monthly', priority: 0.7 },
  { path: '/best-ai-email-tools',
    title: 'Best AI Email Tools in 2026 — Compared | PersonaDraft',
    description: 'A fair round-up of the best AI email tools — Grammarly, Gemini, ChatGPT, Lavender, and Persona — and what each one is best for.',
    changefreq: 'monthly', priority: 0.7 },
];

// path → page-data lookup (no Component; see routes.js for the wired version).
export const MARKETING_PAGE_BY_PATH = Object.fromEntries(MARKETING_PAGES.map((p) => [p.path, p]));
